import { z } from 'zod';
import { isIsoDate } from './dates.js';
import { UNITS } from './constants.js';
import { MOVEMENT_TYPES, PRODUCT_SOURCES, SHOPPING_ORIGINS, SHOPPING_STATUSES } from './types.js';
const isoDate = z
    .string()
    .refine(isIsoDate, { message: 'Data deve estar no formato ISO 8601 (aaaa-mm-dd).' });
const quantity = z.number().positive('Quantidade deve ser maior que zero.').max(100_000);
export const storageInputSchema = z.object({
    name: z.string().trim().min(1, 'Informe o nome da dispensa.').max(60),
    icon: z.string().trim().min(1).max(30).default('box'),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Cor deve ser hexadecimal (#rrggbb).')
        .default('#2f80ed'),
    sortOrder: z.number().int().min(0).optional(),
});
export const storageUpdateSchema = storageInputSchema.partial();
export const storageReorderSchema = z.object({
    ids: z.array(z.string().min(1)).min(1),
});
/**
 * Campos do produto. Nome ou tipo bastam para cadastrar — nada de bloquear por
 * foto, categoria ou tamanho (CLAUDE.md secao 8). Fica como objeto puro para
 * poder derivar as versoes parciais usadas na edicao e na entrada de item.
 */
export const productFieldsSchema = z.object({
    barcode: z.string().trim().min(1).max(64).nullish(),
    /** Rotulo exibido. Em branco, sai de tipo + marca + tamanho. */
    name: z.string().trim().max(120).optional(),
    brand: z.string().trim().max(80).nullish(),
    /** Tipo do produto sem marca nem tamanho ("Leite integral"). */
    productType: z.string().trim().max(80).nullish(),
    /** Tamanho impresso na embalagem ("1 L", "500 g"). */
    size: z.string().trim().max(30).nullish(),
    notes: z.string().trim().max(500).nullish(),
    /** Unidades por embalagem — permite entrada por embalagem e baixa unitaria. */
    packSize: z.number().positive().max(1000).nullish(),
    category: z.string().trim().max(60).nullish(),
    unit: z.enum(UNITS).default('un'),
    photoUrl: z.string().trim().url().max(500).nullish(),
    defaultShelfLifeDays: z.number().int().positive().max(3650).nullish(),
    /** De onde vieram os dados: digitados, catalogo publico ou foto lida pela IA. */
    source: z.enum(PRODUCT_SOURCES).optional(),
});
/** Nome ou tipo: sem nenhum dos dois nao ha como chamar o produto na lista. */
export const hasProductLabel = (value) => Boolean((value.name ?? '').trim() || (value.productType ?? '').trim());
export const productInputSchema = productFieldsSchema.refine(hasProductLabel, {
    message: 'Informe o nome ou o tipo do produto.',
    path: ['name'],
});
export const productUpdateSchema = productFieldsSchema.partial();
/**
 * Entrada de item. Aceita um produto ja existente (`productId`), um codigo de
 * barras (`barcode`, criando/reaproveitando o produto do catalogo) ou um
 * produto novo digitado na hora (`product`).
 */
export const stockEntrySchema = z
    .object({
    productId: z.string().min(1).optional(),
    barcode: z.string().trim().min(1).max(64).optional(),
    product: productFieldsSchema.partial({ unit: true }).optional(),
    storageId: z.string().min(1, 'Escolha a dispensa.'),
    quantity: quantity.default(1),
    entryDate: isoDate.optional(),
    expiresAt: isoDate.nullish(),
    restockOnDepletion: z.boolean().default(true),
    note: z.string().trim().max(200).nullish(),
})
    .refine((value) => Boolean(value.productId ?? value.barcode) || (value.product ? hasProductLabel(value.product) : false), { message: 'Informe o produto (id, codigo de barras ou nome).' });
export const stockItemUpdateSchema = z.object({
    quantity: z.number().min(0).max(100_000).optional(),
    expiresAt: isoDate.nullish(),
    expiryEstimated: z.boolean().optional(),
    restockOnDepletion: z.boolean().optional(),
    note: z.string().trim().max(200).nullish(),
});
export const consumeSchema = z.object({
    mode: z.enum(['decrement', 'deplete']).default('decrement'),
    quantity: quantity.optional(),
    /** `discard` marca a movimentacao como descarte em vez de consumo. */
    reason: z.enum(['consumed', 'discarded']).default('consumed'),
    note: z.string().trim().max(200).nullish(),
});
export const transferSchema = z.object({
    toStorageId: z.string().min(1, 'Escolha a dispensa de destino.'),
    quantity: quantity.optional(),
});
export const shoppingListInputSchema = z
    .object({
    productId: z.string().min(1).optional(),
    label: z.string().trim().min(1).max(120).optional(),
    suggestedQuantity: quantity.default(1),
    origin: z.enum(SHOPPING_ORIGINS).default('manual'),
})
    .refine((value) => Boolean(value.productId ?? value.label), {
    message: 'Informe o produto ou um nome para o item.',
});
export const shoppingListUpdateSchema = z.object({
    status: z.enum(SHOPPING_STATUSES).optional(),
    suggestedQuantity: quantity.optional(),
    label: z.string().trim().min(1).max(120).optional(),
});
/**
 * Pergunta do modo turbo: o que o usuario vai fazer, em texto livre. O estoque
 * nao vem do cliente — quem monta e o servidor, com o que esta no banco.
 */
export const turboAskSchema = z.object({
    prompt: z
        .string()
        .trim()
        .min(3, 'Diga o que voce vai fazer.')
        .max(500, 'Resuma o pedido em ate 500 caracteres.'),
});
/** Aceitar sugestoes do turbo em lote — uma chamada, varias linhas. */
export const shoppingListBulkSchema = z.object({
    entries: z.array(shoppingListInputSchema).min(1).max(50),
});
export const settingsUpdateSchema = z.object({
    expiryAlertDays: z.number().int().min(0).max(90).optional(),
    lastStorageId: z.string().min(1).nullish(),
    externalCatalogEnabled: z.boolean().optional(),
    photoAiEnabled: z.boolean().optional(),
});
export const shelfLifePresetInputSchema = z.object({
    category: z.string().trim().min(1).max(60),
    days: z.number().int().positive().max(3650),
});
export const movementQuerySchema = z.object({
    type: z.enum(MOVEMENT_TYPES).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
/**
 * Foto enviada para identificacao por IA. Chega como data URL do canvas
 * (`data:image/jpeg;base64,...`); o limite de 6 MB acompanha o bodyLimit da API.
 */
export const photoIdentifySchema = z.object({
    /**
     * Codigo de barras ja conhecido pela tela (lido no scanner ou digitado).
     * Vai junto para a resposta ja vir vinculada ao produto certo.
     */
    barcode: z.string().trim().min(1).max(64).optional(),
    image: z
        .string()
        .max(6 * 1024 * 1024, 'Foto grande demais — reduza a resolucao.')
        .regex(/^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/, 'Formato de imagem invalido.'),
});
/** Login da area administrativa (fase 1: um usuario, sem cadastro). */
export const adminLoginSchema = z.object({
    username: z.string().trim().min(1, 'Informe o usuario.').max(60),
    password: z.string().min(1, 'Informe a senha.').max(200),
});
/**
 * Chave e modelo da OpenAI. String vazia apaga a chave gravada (voltando para
 * a do ambiente, se existir); `undefined` deixa como esta.
 */
export const adminConfigSchema = z.object({
    openaiApiKey: z.string().trim().max(300).nullish(),
    visionModel: z.string().trim().max(80).nullish(),
});
export const adminPasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Informe a senha atual.').max(200),
    newPassword: z
        .string()
        .min(8, 'A nova senha precisa de ao menos 8 caracteres.')
        .max(200),
});
/** Logo ja recortado pela tela de administracao, como data URL de PNG. */
export const brandingLogoSchema = z.object({
    image: z
        .string()
        .max(4 * 1024 * 1024, 'Imagem grande demais.')
        .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/, 'Formato de imagem invalido.'),
});
//# sourceMappingURL=schemas.js.map